import type { CreateInvestigationsAncMutation } from "$routes/patient/[:patientId]/inpatient/investigations/_lib/__generated__/use-create-investigation-activities.gql";
import type {
  JSONSchema,
  OrderedJSONSchema,
} from "@elephant-healthcare/yupify";
import type { VisitInvestigationActivity } from "elephant-web/gql";
import type { InvestigationActivity } from "./investigation-activity-type";

const JSONObjectType: JSONSchema["type"] = "object";

interface OrderedJSONSchemaWithValue
  extends Omit<OrderedJSONSchema, "orderedProperties"> {
  investigationMeta: {
    coding: {
      loinc?: {
        code: string;
      };
    };
  };
  orderedProperties?: OrderedJSONSchemaWithValue[];
  value?: string;
}

/**
 * The logic in this file is copied/re-used from api-investigation
 */

function meetsRequiredCondition(value: unknown, valueRequired: boolean) {
  if (valueRequired) {
    return !!value;
  }

  return true;
}

function hasNestedValue(orderedProp: OrderedJSONSchemaWithValue) {
  if (
    orderedProp.type === JSONObjectType &&
    Array.isArray(orderedProp.orderedProperties)
  ) {
    return orderedProp.orderedProperties.map(hasNestedValue).find(Boolean);
  }

  // Sub properties with the 'name' 'value' are the property which hold
  // actual user values.
  // Other sub properties like 'unit' often have a default value
  // so don't use them for the assertion.
  const hasValue =
    orderedProp.name === "value" && orderedProp.value !== undefined;

  return hasValue;
}

function getInvestigationProperties(
  orderedProp: OrderedJSONSchemaWithValue,
  loincCodes: string[],
  options: { valueRequired: boolean } = { valueRequired: true },
) {
  const { valueRequired } = options;
  const maybeCode = orderedProp.investigationMeta?.coding?.loinc?.code;
  const maybeValue = orderedProp.value;
  const isLoincProperty = loincCodes.indexOf(maybeCode) > -1;

  if (isLoincProperty && meetsRequiredCondition(maybeValue, valueRequired)) {
    return [orderedProp];
  }

  // We also have properties, such as UnitInputs, which have their 'value' nested
  if (
    isLoincProperty &&
    !meetsRequiredCondition(maybeValue, valueRequired) &&
    orderedProp.type === JSONObjectType &&
    Array.isArray(orderedProp.orderedProperties) &&
    hasNestedValue(orderedProp)
  ) {
    return [orderedProp];
  }

  if (
    orderedProp.type === JSONObjectType &&
    Array.isArray(orderedProp.orderedProperties)
  ) {
    const properties = orderedProp.orderedProperties.flatMap((subOrderedProp) =>
      getInvestigationProperties(subOrderedProp, loincCodes, options),
    );

    return properties;
  }

  // base case
  return [];
}

const getValueAndNameFromProperty = (property: OrderedJSONSchemaWithValue) => {
  if (property.value !== undefined) {
    return { name: property.name, value: property.value };
  }

  if (property.type === JSONObjectType && property.orderedProperties) {
    const props = property.orderedProperties.map(getValueAndNameFromProperty);

    if (props.length > 1) {
      // Handle when we have nested properties such as 'UnitInput'
      // and the value we're interested in resides within the property with a specific name
      // In this case, also include the unit if it is present
      const value = props.find((prop) => prop?.name === "value");
      const unit = props.find((prop) => prop?.name === "unit");
      return {
        ...value,
        unit: unit?.value,
      };
    }

    return props[0];
  }

  return null;
};

export const getOutcomeValuesFromInvestigation = (
  loincCode: string,
  investigation:
    | InvestigationActivity
    | VisitInvestigationActivity
    | CreateInvestigationsAncMutation["createInvestigations"][0],
) => {
  const propertiesWithValues = investigation.outcome.orderedProperties.flatMap(
    (prop: OrderedJSONSchemaWithValue) =>
      getInvestigationProperties(prop, [loincCode], { valueRequired: true }),
  );

  if (!propertiesWithValues?.length) {
    return undefined;
  }

  const property = propertiesWithValues[0];

  const { value } = getValueAndNameFromProperty(property) || {};

  return value;
};
